/**
 * The coach agent's brain: rule-based reactions to what's happening on the board.
 * Deterministic, offline, and kid-safe. It never repeats itself back-to-back and
 * doesn't chatter: unprompted lines are rate-limited, important ones get through.
 */
import type { PlaceProblem } from '../core/board';
import type { SimEvent } from '../sim/sim';
import type { Mood, Sparky } from './sparky';

interface Line {
  text: string;
  mood: Mood;
  /** Important lines skip the rate limit. */
  urgent?: boolean;
  /** Only say this once per session. */
  once?: boolean;
}

const EVENT_LINES: Partial<Record<string, Line>> = {
  'lit:lamp': {
    text: 'You made light! Electricity is zooming around your loop. ⚡',
    mood: 'wow',
    once: true,
  },
  'lit:led': { text: 'The LED is glowing! LEDs only let electricity go one way.', mood: 'wow', once: true },
  'spin:motor': { text: 'It’s spinning! Motors turn electricity into motion.', mood: 'wow', once: true },
  'buzz:buzzer': { text: 'Bzzzz! That’s the buzzer singing.', mood: 'happy', once: true },
  'short:battery': {
    text: 'Whoa, too hot! The electricity took a shortcut straight back to the battery. Put a bulb or a motor in the loop.',
    mood: 'oops',
    urgent: true,
  },
  'tripped:battery': {
    text: 'The battery switched itself off to stay safe. Take away the shortcut and let it cool down.',
    mood: 'oops',
    urgent: true,
  },
  'cool:battery': { text: 'Phew! The battery is cool again.', mood: 'happy' },
  'reversed:led': {
    text: 'LEDs are one-way streets! Try flipping it around. 🔄',
    mood: 'think',
    urgent: true,
  },
  'launch:motor': { text: 'Woohoo! Liftoff! 🚀', mood: 'wow', urgent: true },
  'wrongway:motor': {
    text: 'The fan is spinning backwards, so it pushes air up instead of down. Flip the battery or the motor to make it fly!',
    mood: 'think',
    urgent: true,
  },
};

const PROBLEM_LINES: Record<PlaceProblem | 'stuck' | 'locked', string> = {
  'snap-on-body': 'Snaps need to land on another snap or on an empty dot.',
  outside: 'Oops! That part needs to fit on the board.',
  'too-high': 'That stack is too tall! Try another spot.',
  stuck: 'Take the piece on top off first!',
  locked: 'That part is glued down for this level. Use the parts in the tray!',
};

export class Brain {
  private lastText = '';
  private lastAt = -1e9;
  private said = new Set<string>();
  /** Seconds between unprompted lines. */
  gap = 7;
  muted = false;

  constructor(private sparky: Sparky) {}

  say(text: string, mood: Mood = 'happy', urgent = true) {
    const now = performance.now() / 1000;
    if (!urgent && (now - this.lastAt < this.gap || text === this.lastText)) return false;
    if (text === this.lastText && now - this.lastAt < 4) return false;
    this.lastText = text;
    this.lastAt = now;
    this.sparky.say(text, mood);
    return true;
  }

  onSim(e: SimEvent) {
    if (this.muted) return;
    const key = `${e.kind}:${e.type}`;
    const line = EVENT_LINES[key];
    if (!line) return;
    if (line.once && this.said.has(key)) return;
    if (this.say(line.text, line.mood, line.urgent)) this.said.add(key);
    if (e.kind === 'launch') this.sparky.cheer();
  }

  onProblem(p: PlaceProblem | 'stuck' | 'locked') {
    this.say(PROBLEM_LINES[p], 'oops', true);
  }
}
